import { ALL_PRODUCTS as PRODUCTS } from './products'
import { SHIRT_ID, ShirtSelection, isShirtSelection, shirtVariant, shirtVariants } from './apparel'
export type CartLine = { productId: string; qty: number; selection?: ShirtSelection }
export const lineKey = (line: Pick<CartLine, 'productId' | 'selection'>) => `${line.productId}:${line.selection?.color ?? ''}:${line.selection?.size ?? ''}`
export function normalizeCart(saved: unknown): CartLine[] {
  if (!Array.isArray(saved)) return []
  const found = new Map<string, CartLine>()
  for (const line of saved) {
    if (!line || !PRODUCTS.some(p => p.id === line.productId) || !Number.isSafeInteger(line.qty) || line.qty <= 0) continue
    if (line.productId === SHIRT_ID && !isShirtSelection(line.selection)) continue
    const item: CartLine = { productId: line.productId, qty: Math.min(99, line.qty), ...(line.productId === SHIRT_ID ? { selection: line.selection } : {}) }
    const key = lineKey(item)
    const existing = found.get(key)
    if (existing) existing.qty = Math.min(99, existing.qty + item.qty)
    else found.set(key, item)
  }
  return [...found.values()]
}
export function checkoutItems(cart: CartLine[]) {
  return normalizeCart(cart).map(line => {
    if (line.productId === SHIRT_ID) {
      const v = line.selection && shirtVariant(line.selection.color)
      if (!v) throw new Error('This shirt color is currently unavailable.')
      return { id: v.id, quantity: line.qty, properties: { Size: line.selection!.size } }
    }
    return { id: PRODUCTS.find(p => p.id === line.productId)!.variantId, quantity: line.qty }
  })
}
type ShopifyCartItem = { key: string; variant_id: number | string; quantity: number; properties?: Record<string, string> | null }
export function reconcileShopifyCart(existing: ShopifyCartItem[], items: ReturnType<typeof checkoutItems>) {
  const signature = (id: string | number, size?: string) => `${id}:${size ?? ''}`
  const remaining = new Map(items.map(item => [signature(item.id, item.properties?.Size), item]))
  const managedIds = new Set([...PRODUCTS.filter(p => p.variantId).map(p => p.variantId), ...shirtVariants().map(v => v.id), ...items.map(item => item.id)])
  const updates: Record<string, number> = {}
  for (const line of existing) {
    if (!managedIds.has(String(line.variant_id))) continue
    const key = signature(line.variant_id, line.properties?.Size)
    const desired = remaining.get(key)
    const quantity = desired?.quantity ?? 0
    if (line.quantity !== quantity) updates[line.key] = quantity
    remaining.delete(key)
  }
  return { updates, additions: [...remaining.values()] }
}
export async function checkoutShirtCart(cart: CartLine[]) {
  const items = checkoutItems(cart)
  if (!items.length) throw new Error('Your cart is empty.')
  if (!location.hostname.endsWith('.myshopify.com') && location.hostname !== 'shop.sfalexandria.com') {
    throw new Error('Checkout is available on shop.sfalexandria.com.')
  }
  // Mirror this storefront's quantities without discarding unrelated Shopify
  // items. An abandoned checkout can be retried without adding duplicates.
  const current = await fetch('/cart.js')
  if (!current.ok) throw new Error('Could not load your cart. Please try again.')
  const { items: existing } = await current.json() as { items: ShopifyCartItem[] }
  const { updates, additions } = reconcileShopifyCart(existing, items)
  const post = async (url: string, body: unknown) => {
    const result = await fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    })
    if (!result.ok) {
      const error = await result.json().catch(() => ({}))
      throw new Error(error.description || 'Could not update your cart. Please try again.')
    }
  }
  if (Object.keys(updates).length) await post('/cart/update.js', { updates })
  if (additions.length) await post('/cart/add.js', { items: additions })
  location.assign('/checkout')
}
